import { ref, reactive, computed, watch, nextTick } from 'vue'
import { defineStore } from 'pinia'
import { v4 as uuidv4 } from 'uuid'

export enum DieCutMaskShape {
  Rectangle = 'rectangle',
  Triangle = 'triangle',
  Circle = 'circle',
}

export interface TextLayer {
  id: string
  text: string
  font: string
  fontWeight: string
  fontSize: number // pt
  color: string
  align: 'left' | 'center' | 'right' | 'justify'
  positionX: number
  positionY: number
  width: number // mm
  height: number // mm
  zIndex: number
}

export const useDesignerStore = defineStore('designer', () => {
  // 👇 guard, aby sa pri hydratácii neposielali spätné emity a neprepisoval hidden input
  const hydrating = ref(false)
  const canvasReady = ref(false)
  let canvasReadyResolver: (() => void) | null = null
  const canvasReadyPromise = new Promise<void>((resolve) => { canvasReadyResolver = resolve })
  function whenCanvasReady() { return canvasReady.value ? Promise.resolve() : canvasReadyPromise }
  const scaleTick = ref(0)
  function bumpScale() { scaleTick.value++ }


  const canvasRef = ref<HTMLElement | null>(null)
  const outerWrapperRef = ref<HTMLElement | null>(null)

  const setCanvasRef = (el: HTMLElement | null) => {
    canvasRef.value = el
    nextTick(() => {
      canvasReady.value = true
      if (canvasReadyResolver) canvasReadyResolver()
    })
  }

  function setOuterWrapperRef(el: HTMLElement | null) {
    outerWrapperRef.value = el
  }

  const shapes = ref<any[]>([])

  // sem si jednotlivé shape komponenty posielajú vždy čerstvé SVG
  const dieCutSvg = ref<string>('')

  // Die cut = orezová línia v mm
  const dieCutMask = ref({
    width: 300, // mm
    height: 300, // mm
    radiusX: 10,
    radiusY: 10,
    positionX: 150,
    positionY: 100,
    zIndex: 10,
    type: 'rectangle' as 'rectangle' | 'triangle' | 'circle',
    bleedOffset: 3, // mm
  })

  // Label image (pozícia a rozmery v mm)
  const labelImage = reactive({
    imageSrc: '',
    width: 200,
    height: 200,
    positionX: 50,
    positionY: 50,
    fit: 'contain' as 'contain' | 'cover'
  })

  // Logický “mm” priestor (nie pixely obrazovky) – ukladáme tiež (stabilita layoutu)
  const canvasSizeInMm = reactive({
    width: 800,
    height: 600,
  })

  const setShape = (shape: DieCutMaskShape) => {
    dieCutMask.value.type = shape

    if (shape === 'rectangle') {
      dieCutMask.value.width = 300
      dieCutMask.value.height = 200
      dieCutMask.value.radiusX = 10
      dieCutMask.value.radiusY = 10
    } else if (shape === 'triangle') {
      dieCutMask.value.width = 300
      dieCutMask.value.height = 260
      dieCutMask.value.radiusX = 0
      dieCutMask.value.radiusY = 0
    } else if (shape === 'circle') {
      dieCutMask.value.width = 280
      dieCutMask.value.height = 280
      dieCutMask.value.radiusX = 0
      dieCutMask.value.radiusY = 0
    }
  }

  const textLayers = ref<TextLayer[]>([
    {
      id: uuidv4(),
      text: 'Ukážkový text',
      font: 'Arial',
      fontWeight: 'bold',
      fontSize: 24, // pt
      color: '#000000',
      align: 'center',
      positionX: canvasSizeInMm.width / 2,
      positionY: canvasSizeInMm.height / 2,
      width: 100, // mm
      height: 50, // mm
      zIndex: 20,
    },
  ])

  // Serializovaný stav – toto ide do hidden poľa a na API
  const designerConfig = computed(() => ({
    canvasSizeInMm: { ...canvasSizeInMm }, // 👈 ukladáme, aby boli mm konzistentné naprieč zariadeniami
    labelImage: {
      position: {
        x: +labelImage.positionX.toFixed(2),
        y: +labelImage.positionY.toFixed(2),
      },
      dimensions: {
        width: +labelImage.width.toFixed(2),
        height: +labelImage.height.toFixed(2),
      },
      imageSrc: labelImage.imageSrc,
      fit: labelImage.fit
    },
    dieCutMask: {
      type: dieCutMask.value.type,
      svg: dieCutSvg.value,
      position: {
        x: +dieCutMask.value.positionX.toFixed(2),
        y: +dieCutMask.value.positionY.toFixed(2),
      },
      dimensions: {
        width: +dieCutMask.value.width.toFixed(2),
        height: +dieCutMask.value.height.toFixed(2),
      },
      borderRadius: {
        rx: +dieCutMask.value.radiusX.toFixed(2),
        ry: +dieCutMask.value.radiusY.toFixed(2),
      },
      bleedOffset: +dieCutMask.value.bleedOffset.toFixed(2),
    },
    textLayers: textLayers.value,
  }))

  const addTextLayer = (config?: Partial<(typeof textLayers.value)[0]>) => {
    textLayers.value.push({
      id: uuidv4(),
      text: 'Nový text',
      font: 'Arial',
      fontWeight: 'regular',
      fontSize: 20,
      color: '#000000',
      align: 'left',
      positionX: 100,
      positionY: 100,
      width: 100,
      height: 50,
      zIndex: 20,
      ...(config || {}),
    })
  }

  const deleteTextLayer = (id: string) => {
    const index = textLayers.value.findIndex((layer) => layer.id === id)
    if (index !== -1) {
      textLayers.value.splice(index, 1)
    }
  }

  // Hydratácia z JSONu (DB / inline)
  function applyConfig(cfg: any) {
    console.log("[applyConfig] incoming labelImage", cfg?.labelImage);
    hydrating.value = true
    try {
      // canvas mm – ak je v JSONe, použijeme ho
      if (cfg?.canvasSizeInMm?.width && cfg?.canvasSizeInMm?.height) {
        canvasSizeInMm.width = Number(cfg.canvasSizeInMm.width)
        canvasSizeInMm.height = Number(cfg.canvasSizeInMm.height)
      }

      // die-cut
      if (cfg?.dieCutMask) {
        const dc = cfg.dieCutMask
        dieCutMask.value.type = (dc.type ?? 'rectangle') as 'rectangle' | 'triangle' | 'circle'
        dieCutMask.value.width = Number(dc.dimensions?.width ?? dc.width ?? dieCutMask.value.width)
        dieCutMask.value.height = Number(dc.dimensions?.height ?? dc.height ?? dieCutMask.value.height)
        dieCutMask.value.positionX = Number(dc.position?.x ?? dieCutMask.value.positionX)
        dieCutMask.value.positionY = Number(dc.position?.y ?? dieCutMask.value.positionY)
        dieCutMask.value.radiusX = Number(dc.borderRadius?.rx ?? dc.radiusX ?? dieCutMask.value.radiusX)
        dieCutMask.value.radiusY = Number(dc.borderRadius?.ry ?? dc.radiusY ?? dieCutMask.value.radiusY)
        dieCutMask.value.bleedOffset = Number(dc.bleedOffset ?? dieCutMask.value.bleedOffset)
        dieCutSvg.value = String(dc.svg ?? '')
      }

      // label image
      // label image
      if (cfg.labelImage) {
        const li = cfg.labelImage
        console.log('[applyConfig] incoming labelImage', li)

        labelImage.imageSrc = String(li.imageSrc ?? labelImage.imageSrc)
        labelImage.width    = Number(li.dimensions?.width  ?? li.width  ?? labelImage.width)
        labelImage.height   = Number(li.dimensions?.height ?? li.height ?? labelImage.height)
        labelImage.positionX= Number(li.position?.x ?? li.positionX ?? labelImage.positionX)
        labelImage.positionY= Number(li.position?.y ?? li.positionY ?? labelImage.positionY)
        labelImage.fit       = (li.fit === 'cover' ? 'cover' : 'contain')

        console.log('[applyConfig] stored labelImage', {
          x: labelImage.positionX,
          y: labelImage.positionY,
        })
      }

      // text layers
      if (Array.isArray(cfg?.textLayers)) {
        textLayers.value = cfg.textLayers.map((t: any) => ({
          id: t.id,
          text: String(t.text ?? ''),
          font: String(t.font ?? 'Arial'),
          fontWeight: String(t.fontWeight ?? '400'),
          fontSize: Number(t.fontSize ?? 20),
          color: String(t.color ?? '#000000'),
          align: (t.align ?? 'left') as TextLayer['align'],
          positionX: Number(t.positionX ?? 100),
          positionY: Number(t.positionY ?? 100),
          width: Number(t.width ?? 100),
          height: Number(t.height ?? 50),
          zIndex: Number(t.zIndex ?? 20),
        }))
      }
    } finally {
      // necháme dom/px výpočty dobehnúť a potom zrušíme guard
      nextTick(() => {
        hydrating.value = false
      })
    }

    console.log("[applyConfig] stored labelImage", {
      positionX: labelImage.positionX,
      positionY: labelImage.positionY
    });

    // 🔥 CRITICAL FIX – musí byť ÚPLNE NA KONCI
    // vyvolá scaleTick → LabelImage/TextLayer urobia recomputeFromProps()
    bumpScale();

  }

  // Sync do hidden inputu len keď *ne*hydratujeme
  watch(
    designerConfig,
    (val) => {
      if ((window as any).DESIGNER_UPDATE_HIDDEN && !hydrating.value) {
        ;(window as any).DESIGNER_UPDATE_HIDDEN(val)
      }
    },
    { deep: true, immediate: true }
  )

  return {
    // state
    hydrating,
    canvasReady,
    shapes,
    dieCutMask,
    dieCutSvg,
    labelImage,
    canvasSizeInMm,
    textLayers,
    canvasRef,

    // derived
    designerConfig,
    whenCanvasReady,
    scaleTick,
    bumpScale,

    // actions
    setCanvasRef,
    setOuterWrapperRef,
    outerWrapperRef,
    setShape,
    addTextLayer,
    deleteTextLayer,
    applyConfig,
  }
})
